import React from 'react';
import './Footer.css';

const MapIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>
);

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer" role="contentinfo">
      {/* Main Footer Content */}
      <div className="footer-main">
        <div className="container">
          <div className="footer-content-grid">
            
            {/* Left Side - Contact & Location */}
            <div className="contact-location-section">
              
              {/* Contact Information */}
              <div className="contact-info">
                <h4 className="section-heading">Contact Information</h4>
                <div className="contact-details">
                  <div className="contact-item">
                    <span className="contact-label">Phone:</span>
                    <a 
                      href="tel:+13149616100" 
                      className="contact-link"
                      aria-label="Call Dixon Financial Services at 314-961-6100"
                    >
                      314-961-6100
                    </a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-label">Email:</span>
                    <a 
                      href="mailto:rob@dixonfinancialservices.com" 
                      className="contact-link"
                      aria-label="Email Dixon Financial Services"
                    >
                      rob@dixonfinancialservices.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Office Location */}
              <div className="location-info">
                <h4 className="section-heading">Office Location</h4>
                <div className="office-address">
                  <address>
                    16 N. Gore Ave, Suite 207<br />
                    Saint Louis, MO 63119
                  </address>
                  <a 
                    href="https://maps.google.com?q=16+N.+Gore+Avenue,+Saint+Louis,+MO+63119" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="directions-link"
                    aria-label="Get directions to Dixon Financial Services office"
                  >
                    <MapIcon />
                    Get Directions
                  </a>
                </div>

                {/* Business Hours */}
                <div className="business-hours">
                  <h5 className="hours-heading">Business Hours</h5>
                  <div className="hours-list">
                    <div className="hours-item">
                      <span className="day">Monday - Friday:</span>
                      <span className="time">9:00 AM - 5:00 PM</span>
                    </div>
                    <div className="hours-item">
                      <span className="day">Saturday - Sunday:</span>
                      <span className="time">By Appointment</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Legal & Regulatory Information */}
            <div className="legal-regulatory-section">
              <h4 className="section-heading">Important Regulatory Information</h4>
              
              {/* Regulatory Links */}
              <div className="regulatory-links">
                <a 
                  href="https://brokercheck.finra.org/individual/summary/2376039" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="regulatory-link"
                  aria-label="FINRA BrokerCheck"
                >
                  FINRA's BrokerCheck
                </a>
                <a 
                  href="https://www.joincambridge.com/investors/cambridge-disclosures/form-crs/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="regulatory-link"
                  aria-label="View Cambridge Form CRS disclosure document"
                >
                  Cambridge Form CRS
                </a>
                <p className="regulatory-text">
                  Securities offered through Cambridge Investment Research Inc, a Broker/Dealer, Member{' '}
                  <a 
                    href="https://www.finra.org" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-link"
                    aria-label="Visit FINRA website"
                  >
                    FINRA
                  </a>/
                  <a 
                    href="https://www.sipc.org" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-link"
                    aria-label="Visit SIPC website"
                  >
                    SIPC
                  </a>.
                </p>
              </div>

              {/* Legal Disclosures */}
              <div className="legal-disclosures">
                <p className="legal-text">
                  Investment advisory services offered through Cambridge Investment Research Advisors, Inc., a Registered Investment Adviser. 
                  Dixon Financial and Cambridge are not affiliated. Securities offered through Cambridge Investment Research Inc., 
                  Member FINRA/SIPC, to residents of MO, IL, CA, and NC.
                </p>
                
                <p className="legal-text">
                  The information provided on this website is intended for general informational purposes only and should not be 
                  considered as tax, legal, or investment advice. Please consult your own legal, tax, or financial advisor for 
                  guidance tailored to your individual situation. The opinions expressed here are those of the author and may not 
                  reflect the views of Cambridge or Dixon Financial. Nothing on this website should be considered a solicitation 
                  or offer to buy or sell any security.
                </p>
                
                <p className="legal-text">
                  We take your privacy seriously. As of January 1, 2020, the{' '}
                  <a 
                    href="/privacy-policy" 
                    className="inline-link"
                    aria-label="View California Consumer Privacy Act information"
                  >
                    California Consumer Privacy Act (CCPA)
                  </a>{' '}
                  provides you with certain rights.{' '}
                  <a 
                    href="/do-not-sell" 
                    className="inline-link"
                    aria-label="Do not sell my personal information"
                  >
                    Do not sell my personal information
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            <div className="copyright">
              <p>&copy; {currentYear} Dixon Financial Services. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 